// ** MUI Imports
import Box from '@mui/material/Box'
import { Theme, styled } from '@mui/material/styles'
import TextField from '@mui/material/TextField'
import IconButton from '@mui/material/IconButton'
import useMediaQuery from '@mui/material/useMediaQuery'
import InputAdornment from '@mui/material/InputAdornment'

import React from 'react'

// ** Next Import
import Link from 'next/link'

// ** Icons Imports
import Menu from 'mdi-material-ui/Menu'
import Magnify from 'mdi-material-ui/Magnify'

// ** Navigation Imports
import VerticalNavItems from 'src/navigation/vertical'

// ** Type Import
import { Settings } from 'src/@core/context/settingsContext'

// ** Components
import ModeToggler from 'src/@core/layouts/components/shared-components/ModeToggler'
import UserDropdown from 'src/@core/layouts/components/shared-components/UserDropdown'
import NotificationDropdown from 'src/@core/layouts/components/shared-components/NotificationDropdown'
import LanguageDropdown from 'src/@core/layouts/components/shared-components/LanguageDropdown'
import Navigation from '../horizontal/navigation'
import ChatClaude from './ChatClaude'
import { HelpOutline, Science } from '@mui/icons-material'
import HelpPdf from '@/@core/layouts/components/shared-components/Help'
import FilterSelect from '@/@core/layouts/components/shared-components/FilterSelect'
import { ExternalLinks } from '@/@core/services/api'
import { isWindow } from '@/@core/utils/utils'
import ReportSearchIcon from '@/@core/layouts/components/shared-components/ReportSearchIcon'
import SystemMonitorIcon from '@/@core/layouts/components/shared-components/SystemMonitorIcon'
import { useContext } from 'react'
import { AbilityContext } from '@/layouts/components/acl/Can'
import {
  AlphaKBox,
  AlphaKBoxOutline,
  AlphaKCircleOutline
} from 'mdi-material-ui'
import { Tooltip } from '@mui/material'
import { Router, useRouter } from 'next/router'
import { useTranslation } from 'react-i18next'

interface Props {
  hidden: boolean
  settings: Settings
  toggleNavVisibility: () => void
  saveSettings: (values: Settings) => void
}

const LinkStyled = styled(Link)({
  display: 'flex',
  alignItems: 'center',
  textDecoration: 'none'
})

const AppBarContent = (props: Props) => {
  const router = useRouter()
  const { t } = useTranslation()

  // ** Props
  const { hidden, settings, saveSettings, toggleNavVisibility } = props
  const [value, setValue] = React.useState('')

  const ability = useContext(AbilityContext)
  let type = 'window'

  if (/(micromessenger)/i.test(navigator.userAgent)) {
    // alert('微信')
    type = 'mobile'
  } else {
    // alert('普通浏览器')

    // 判断h5还是pc true就是h5
    const client =
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      )

    if (client) {
      // alert('h5')
      type = 'mobile'
    } else {
      // alert('pc')
      type = 'window'
    }
  }
  const sysKey = React.useMemo(() => {
    const secretKey = process.env.SYSTEM
    return secretKey
  }, [])

  // ** Hook
  const hiddenSm = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'))

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}
    >
      <LinkStyled href="/" passHref>
        <img
          width={150}
          src={
            sysKey === 'shengxiang'
              ? '/images/logos/圣湘.png'
              : '/images/logos/genomelight_logo.png'
          }
          alt=""
        />
      </LinkStyled>

      <Box
        className="actions-left"
        sx={{ mr: 2, display: 'flex', alignItems: 'center' }}
      >
        {hidden && type === 'window' ? (
          <>
            <Box
              className="actions-left"
              sx={{ mr: 2, display: 'flex', alignItems: 'center' }}
            >
              <Navigation
                settings={props.settings}
                horizontalNavItems={VerticalNavItems()}
              />
            </Box>
          </>
        ) : (
          <>
            <IconButton
              color="inherit"
              onClick={toggleNavVisibility}
              sx={{ ml: -2.75, ...(hiddenSm ? {} : { mr: 3.5 }) }}
            >
              <Menu />
            </IconButton>
          </>
        )}
      </Box>

      <Box
        className="actions-right"
        sx={{ display: 'flex', alignItems: 'center' }}
      >
        {isWindow() && (
          <TextField
            size="small"
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 4 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Magnify fontSize="small" />
                </InputAdornment>
              )
            }}
            placeholder={'hg38,4-515999-C-T,PIGG'}
            value={value}
            onChange={(e) => {
              //

              setValue(e.target.value)
            }}
            onKeyDown={(e) => {
              if (e.keyCode === 13 || e.which === 13) {
                // 处理回车键事件
                if (value.includes(',') || value.includes('，')) {
                  const genome_build =
                    value.split(',').join('，').split('，')[0] ?? 'hg38'

                  const variant =
                    value.split(',').join('，').split('，')[1] ?? '4-515999-C-T'

                  const geneName =
                    value.split(',').join('，').split('，')[2] ?? 'PIGG'

                  if (genome_build && variant) {
                    router.push(
                      `/noetica?genome_build=${genome_build}&variant=${variant}&geneName=${geneName}`
                    )

                    setValue('')
                  }

                  e.preventDefault()

                  return false
                }
              }
            }}
          />
        )}

        <HelpPdf />

        {ability && ability.can('read', 'report-search') ? (
          <ReportSearchIcon />
        ) : (
          ''
        )}

        {ability && ability.can('read', 'knowledge-base') ? (
          <Tooltip title={t('Knowledge Base')}>
            <IconButton
              onClick={() => {
                router.push('/knowledge-base')
              }}
            >
              <AlphaKCircleOutline />
            </IconButton>
          </Tooltip>
        ) : (
          ''
        )}

        {isWindow() ? (
          <LanguageDropdown settings={settings} saveSettings={saveSettings} />
        ) : (
          ''
        )}

        {ability && ability.can('read', 'system-monitor') ? (
          <SystemMonitorIcon />
        ) : (
          ''
        )}

        <Tooltip
          title={t(
            'AI Question Answering Window, Interactive Question Answering Tool'
          )}
        >
          <>
            <ChatClaude />
          </>
        </Tooltip>

        <Tooltip
          title={t(
            'AI Noetica database, an integrated AI database for variant annotation, rating, and literature association'
          )}
        >
          <IconButton
            onClick={() => {
              const w = window.open('/noetica', '_blank')
              if (w) w.opener = null
            }}
          >
            <Science sx={{ fontSize: 28, color: 'primary.main' }} />
          </IconButton>
        </Tooltip>

        {/* <ModeToggler settings={settings} saveSettings={saveSettings} /> */}

        {/* <NotificationDropdown /> */}

        {/* <FilterSelect /> */}

        <UserDropdown />
      </Box>
    </Box>
  )
}

export default AppBarContent
